import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, File as FileIcon, Files, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { DownloadsModuleConfig } from '@/types/downloads'
import type { ModuleFieldComponentProps } from '@/types/space-wizard'
import { CATEGORY_NAME_MAX, MAX_CATEGORIES, type DownloadsConfigErrors } from './downloadUtils'

interface Props extends ModuleFieldComponentProps<DownloadsModuleConfig> {
  errors?: DownloadsConfigErrors | null
}

/**
 * The Downloads terms (F-75): repository or a single file, the categories and whether members'
 * items wait for approval. One component for the wizard step and the space edit (PADROES-UI §14);
 * only owner and admin get here, and the API refuses the save for anyone else.
 */
export const DownloadsSpaceFields: React.FC<Props> = ({ config, onChange, disabled = false, errors }) => {
  const { t } = useTranslation('downloads')
  const [newName, setNewName] = React.useState('')

  const mode = config.mode ?? 'multi'
  const categories = config.categories ?? []
  const requireApproval = config.requireApproval ?? false

  const setCategories = (next: typeof categories) => onChange({ ...config, categories: next })
  const rename = (index: number, name: string) => setCategories(categories.map((c, i) => (i === index ? { ...c, name } : c)))
  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta
    if (target < 0 || target >= categories.length) return
    const next = [...categories]
    ;[next[index], next[target]] = [next[target], next[index]]
    setCategories(next)
  }
  const addCategory = () => {
    const name = newName.trim()
    if (!name || categories.length >= MAX_CATEGORIES) return
    setCategories([...categories, { name }])
    setNewName('')
  }

  const modes: Array<{ key: 'multi' | 'single'; icon: React.ElementType }> = [
    { key: 'multi', icon: Files },
    { key: 'single', icon: FileIcon }
  ]

  return (
    <div className="min-w-0 space-y-6" data-testid="downloads-config">
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">{t('config.mode.label')}</label>
        <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t('config.mode.label')}>
          {modes.map(({ key, icon: Icon }) => (
            <button key={key} type="button" role="radio" aria-checked={mode === key} disabled={disabled}
              onClick={() => onChange({ ...config, mode: key })}
              data-testid={`downloads-mode-${key}`}
              className={cn(
                'flex min-w-0 items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                mode === key ? 'border-primary bg-primary-50 ring-1 ring-primary' : 'border-gray-200 hover:bg-gray-50',
                disabled && 'cursor-not-allowed opacity-50'
              )}>
              <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', mode === key ? 'text-primary' : 'text-gray-400')} />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">{t(`config.mode.${key}.title`)}</span>
                <span className="block text-xs text-gray-500">{t(`config.mode.${key}.hint`)}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">{t('config.categories.label')}</label>
        <p className="text-xs text-gray-500">{t('config.categories.hint')}</p>
        <ul className="space-y-2">
          {categories.map((category, index) => {
            const inUse = (category.itemsCount ?? 0) > 0
            return (
              <li key={category.id ?? `new-${index}`} className="flex min-w-0 items-center gap-2">
                <Input value={category.name} maxLength={CATEGORY_NAME_MAX} disabled={disabled}
                  onChange={(e) => rename(index, e.target.value)}
                  aria-label={t('config.categories.nameLabel', { n: index + 1 })} className="min-w-0 flex-1" />
                {inUse && (
                  <span className="hidden flex-shrink-0 text-xs text-gray-500 sm:inline">
                    {t('config.categories.inUse', { count: category.itemsCount })}
                  </span>
                )}
                <Button type="button" variant="ghost" size="sm" className="h-9 w-9 flex-shrink-0 p-0"
                  disabled={disabled || index === 0} onClick={() => move(index, -1)}
                  aria-label={t('config.categories.moveUp')} title={t('config.categories.moveUp')}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-9 w-9 flex-shrink-0 p-0"
                  disabled={disabled || index === categories.length - 1} onClick={() => move(index, 1)}
                  aria-label={t('config.categories.moveDown')} title={t('config.categories.moveDown')}>
                  <ArrowDown className="h-4 w-4" />
                </Button>
                {/* A category with items cannot go: the server refuses, and the button says why first. */}
                <Button type="button" variant="ghost" size="sm" className="h-9 w-9 flex-shrink-0 p-0 text-red-600"
                  disabled={disabled || inUse}
                  onClick={() => setCategories(categories.filter((_, i) => i !== index))}
                  aria-label={t('config.categories.remove')}
                  title={inUse ? t('config.categories.removeBlocked', { count: category.itemsCount }) : t('config.categories.remove')}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            )
          })}
        </ul>
        <div className="flex min-w-0 items-center gap-2">
          <Input value={newName} maxLength={CATEGORY_NAME_MAX} disabled={disabled || categories.length >= MAX_CATEGORIES}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory() } }}
            placeholder={t('config.categories.newPlaceholder')} aria-label={t('config.categories.newPlaceholder')}
            className="min-w-0 flex-1" />
          <Button type="button" variant="outline" size="sm" className="flex-shrink-0 gap-1"
            disabled={disabled || !newName.trim() || categories.length >= MAX_CATEGORIES} onClick={addCategory}>
            <Plus className="h-4 w-4" />
            {t('config.categories.add')}
          </Button>
        </div>
        {errors?.categories && <p role="alert" className="text-xs text-red-600">{errors.categories}</p>}
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-700">{t('config.requireApproval.label')}</p>
          <p className="text-xs text-gray-500">{t('config.requireApproval.hint')}</p>
        </div>
        <Switch checked={requireApproval} disabled={disabled} className="data-[state=checked]:bg-primary"
          aria-label={t('config.requireApproval.label')}
          onCheckedChange={(checked) => onChange({ ...config, requireApproval: checked })} />
      </div>
    </div>
  )
}
